import type { TriyoTask, TriyoTaskList } from "@/types/models";

export type TaskCategory = "active" | "completed" | "archived";

/** The three categories Triyo.Web's task list offers, in its order. */
export const TASK_CATEGORIES: TaskCategory[] = ["active", "completed", "archived"];

export const TASK_CATEGORY_LABELS: Record<TaskCategory, string> = {
  active: "Active",
  completed: "Completed",
  archived: "Archived",
};

/** TaskStatus enum values the API uses (TriyoCore.DAL Enums.TaskStatus). */
export const TASK_STATUS = {
  assigned: 1,
  completed: 2,
  approved: 3,
  declined: 4,
  reassigned: 5,
  published: 6,
  comment: 7,
  cancelled: 8,
  inProgress: 9,
  pendingReview: 10,
  pendingApproval: 11,
} as const;

/** Statuses the API counts as "completed" (Completed, Approved, Cancelled). */
const COMPLETED_STATUS_IDS: number[] = [TASK_STATUS.completed, TASK_STATUS.approved, TASK_STATUS.cancelled];

/** The API sends `isArchived`; older payloads used `IsArchived`. */
export function isArchivedTask(task: TriyoTask): boolean {
  return task.isArchived === true || task.IsArchived === true;
}

/**
 * Buckets a task exactly as the API's own category counts do
 * (TaskService.GetUserTasksForWeb): archived wins, then the completed status
 * set, then everything else is active. Falls back to the status text only when
 * StatusId is absent.
 */
export function taskCategory(task: TriyoTask): TaskCategory {
  if (isArchivedTask(task)) return "archived";

  if (typeof task.StatusId === "number") {
    return COMPLETED_STATUS_IDS.includes(task.StatusId) ? "completed" : "active";
  }

  const status = (task.StatusName || task.TaskStatus || task.Status || "").toLowerCase();
  if (status.includes("archive")) return "archived";
  if (
    task.IsCompleted === true ||
    status.includes("complete") ||
    status.includes("cancel") ||
    status.includes("approve") ||
    status.includes("done")
  ) {
    return "completed";
  }
  return "active";
}

/** Badge key for a task row. */
export function taskStatusKey(task: TriyoTask): string {
  const category = taskCategory(task);
  if (category !== "active") return category;
  if (task.IsOverdue) return "overdue";
  if (typeof task.StatusId === "number") {
    return task.StatusId === TASK_STATUS.assigned ? "not_started" : "in_progress";
  }

  const raw = (task.StatusName || task.TaskStatus || task.Status || "").toLowerCase();
  if (raw.includes("overdue") || raw.includes("late")) return "overdue";
  if (raw.includes("progress") || raw.includes("active") || raw.includes("open")) return "in_progress";
  return "not_started";
}

export function groupTasksByCategory(tasks: TriyoTask[]): Record<TaskCategory, TriyoTask[]> {
  const groups: Record<TaskCategory, TriyoTask[]> = { active: [], completed: [], archived: [] };
  for (const task of tasks) groups[taskCategory(task)].push(task);
  return groups;
}

/**
 * Server-computed totals per category. These cover the whole task set, not the
 * loaded page, so the UI can say "showing 8 of 42". Null when the payload
 * carries no counts (the legacy add-in endpoint leaves them at zero).
 */
export function categoryTotals(list: TriyoTaskList | null): Record<TaskCategory, number> | null {
  if (!list) return null;
  const { ActiveTasks, CompletedTasks, ArchivedTasks } = list;
  if (typeof ActiveTasks !== "number" && typeof CompletedTasks !== "number" && typeof ArchivedTasks !== "number") {
    return null;
  }
  return {
    active: ActiveTasks ?? 0,
    completed: CompletedTasks ?? 0,
    archived: ArchivedTasks ?? 0,
  };
}

/** Total rows available across every category, for "load more" decisions. */
export function totalTaskCount(list: TriyoTaskList | null): number | null {
  if (!list) return null;
  if (typeof list.AllTasks === "number" && list.AllTasks > 0) return list.AllTasks;
  if (typeof list.Pagination === "number" && list.Pagination > 0) return list.Pagination;
  return null;
}
